import { type BannerClickParams, CarouselCategoryPromotion } from '@cart-ui/CategoryPromotion/CarouselCategoryPromotion'
import { COUNTRY_CURRENCY_MAP, type ProjectCode } from '@constants'
import { useStmAnalyticsEvent } from '@dni-analysis'
import { useApiCrossBrandMallHomeDetail } from '@hooks-api/fortune/use-api-cross-brand-mall-home-detail'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { STM_COUNTRY_CONFIG } from '../constants'
import { useActiveBrands, useCountryCode } from '../hooks'
import { BrandHotProductSection } from './components/BrandHotProductSection'
import { BrandLogoStrip } from './components/BrandLogoStrip'
import { BrandNewArrivalSection } from './components/BrandNewArrivalSection'
import { BrandPromoSection } from './components/BrandPromoSection'

interface HomePageProps {
  basePath: string
}

const CarouselBannerSkeleton = () => {
  const isMobile = useResponsive('mobile')
  return <Skeleton width="100%" height={isMobile ? 160 : 464} />
}

export const HomePage = ({ basePath }: HomePageProps) => {
  const countryCode = useCountryCode()
  const mallCode = STM_COUNTRY_CONFIG[countryCode]?.mallCode ?? null
  const currency = COUNTRY_CURRENCY_MAP[countryCode]
  const { destructuredData: homeDetail, isLoading: isDetailLoading } = useApiCrossBrandMallHomeDetail({ mall_code: mallCode })

  const activeBrands = useActiveBrands()
  const { destructuredData: userInfo } = useApiCampaignMember(activeBrands[0] ? { code: activeBrands[0].campaignCode } : undefined)
  const userId = userInfo?.platform_uid ?? ''
  const { sendCrossBrandBannerClickEvent, sendBrandNavClickEvent, sendCrossBrandPageViewEvent } = useStmAnalyticsEvent({ userId })

  const handleBannerClick = (params: BannerClickParams) => {
    sendCrossBrandBannerClickEvent({
      bannerId: params.imageId,
      bannerPosition: params.position,
      destinationUrl: params.destinationUrl,
    })
  }

  const handleBrandClick = (projectCode: ProjectCode) => {
    sendBrandNavClickEvent(projectCode)
    sendCrossBrandPageViewEvent(projectCode)
  }

  return (
    <VStack gap={6} py={4}>
      {isDetailLoading
        ? <CarouselBannerSkeleton />
        : <CarouselCategoryPromotion campaignBanners={homeDetail?.banner} onBannerClick={handleBannerClick} />}
      <BrandLogoStrip
        campaigns={homeDetail?.campaigns ?? []}
        isLoading={isDetailLoading}
        basePath={basePath}
        onBrandClick={handleBrandClick}
      />
      <BrandPromoSection
        campaigns={homeDetail?.campaigns ?? []}
        countryCode={countryCode}
        currency={currency}
        isDetailLoading={isDetailLoading}
        basePath={basePath}
      />
      <BrandNewArrivalSection
        campaigns={homeDetail?.campaigns ?? []}
        currency={currency}
        isDetailLoading={isDetailLoading}
        basePath={basePath}
      />
      <BrandHotProductSection
        campaigns={homeDetail?.campaigns ?? []}
        currency={currency}
        isDetailLoading={isDetailLoading}
        basePath={basePath}
      />
    </VStack>
  )
}
